import { useEffect, useRef } from 'react';
import { showTooltip, hideTooltip } from './Tooltip';
import { formatNumber, formatPercent } from '../../lib/fmt';

type Point = { axis: string; value: number };

export default function Radar({
  data,
  size = 300,
  max,
  color = 'var(--chart-c1)',
  targets,
}: {
  data: Array<{ axis?: string; label?: string; value: number | string }>;
  size?: number;
  max?: number;
  color?: string;
  targets?: Array<{ axis?: string; label?: string; value: number | string }>;
}) {
  const parse = (raw: any): number => {
    if (typeof raw === 'number') return Number.isFinite(raw) ? raw : 0;
    if (raw == null) return 0;
    const cleaned = String(raw).replace(/[^\d.,-]/g, '').replace(',', '.');
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
  };

  const normalize = (arr: any[], isTarget = false): Point[] =>
    (Array.isArray(arr) ? arr : [])
      .filter((d) => d && typeof d === 'object')
      .map((d: any, i: number) => {
        const v = isTarget
          ? d?.value ?? d?.target ?? d?.objectif ?? d?.cible
          : d?.value ?? d?.reel ?? d?.['réel'] ?? d?.actual ?? d?.current;
        return {
          axis: String(d?.axis ?? d?.label ?? `Axe ${i + 1}`),
          value: parse(v),
        };
      });

  const points = normalize(data);
  const targetPoints = normalize(targets ?? [], true);

  const allValues = [...points, ...targetPoints].map((p) => p.value);
  const obsMax = Math.max(0, ...allValues.map(Math.abs));
  const autoMax = obsMax <= 1.01 ? 1 : obsMax <= 100 ? 100 : Math.ceil(obsMax / 10) * 10;
  const effectiveMax = max ?? autoMax;

  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 42;
  const n = Math.max(3, points.length);

  const pathRef = useRef<SVGPolygonElement | null>(null);
  useEffect(() => {
    if (!pathRef.current || points.length === 0) return;
    pathRef.current.style.transform = 'scale(0.1)';
    pathRef.current.style.transformOrigin = `${cx}px ${cy}px`;
    pathRef.current.style.transition = 'none';
    requestAnimationFrame(() => {
      if (!pathRef.current) return;
      pathRef.current.style.transition = 'transform 700ms cubic-bezier(.2,.8,.2,1)';
      pathRef.current.style.transform = 'scale(1)';
    });
  }, [points.map((d) => d.axis + d.value).join('|'), cx, cy, points.length]);

  if (points.length === 0) {
    return <div className="card-meta" style={{ textAlign: 'center', padding: 20 }}>Aucune donnée radar</div>;
  }

  const grid = [0.25, 0.5, 0.75, 1];
  const axisPt = (i: number, frac: number): [number, number] => {
    const a = (i / n) * Math.PI * 2 - Math.PI / 2;
    return [cx + r * frac * Math.cos(a), cy + r * frac * Math.sin(a)];
  };

  const polyFor = (pts: Point[]): string =>
    pts
      .map((d, i) => {
        const v = d.value;
        const [x, y] = axisPt(i, Math.max(0, Math.min(1, v / effectiveMax)));
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');

  const formatValue = (v: number) =>
    effectiveMax === 1 ? formatPercent(v) : effectiveMax === 100 ? `${formatNumber(v)} / 100` : formatNumber(v);

  const targetColor = 'var(--chart-c4)';
  const hasTargets = targetPoints.length === points.length && targetPoints.length > 0;

  return (
    <svg width={size} height={size + (hasTargets ? 22 : 0)} viewBox={`0 0 ${size} ${size + (hasTargets ? 22 : 0)}`} style={{ overflow: 'visible', fontFamily: 'inherit' }}>
      {/* Légende (quand deux séries) */}
      {hasTargets && (
        <g transform={`translate(${size / 2 - 90} ${size + 4})`}>
          <rect x={0} y={3} width={14} height={8} fillOpacity={0.4} strokeWidth={2} style={{ fill: color, stroke: color }} />
          <text x={20} y={12} fontSize="11" style={{ fill: 'var(--chart-text)' }}>Réel</text>
          <rect x={90} y={3} width={14} height={8} fillOpacity={0.15} strokeWidth={1.5} strokeDasharray="4 3" style={{ fill: targetColor, stroke: targetColor }} />
          <text x={110} y={12} fontSize="11" style={{ fill: 'var(--chart-text)' }}>Objectif</text>
        </g>
      )}

      {/* Grille */}
      {grid.map((g, i) => (
        <polygon
          key={i}
          fill={i === 0 ? 'var(--c-nav)' : 'none'}
          fillOpacity={0.4}
          strokeDasharray={i === grid.length - 1 ? undefined : '2 2'}
          style={{ stroke: 'var(--chart-grid)' }}
          points={points.map((_, j) => axisPt(j, g).map((n) => n.toFixed(1)).join(',')).join(' ')}
        />
      ))}

      {/* Axes */}
      {points.map((_, i) => {
        const [x, y] = axisPt(i, 1);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} style={{ stroke: 'var(--chart-grid)' }} />;
      })}

      {/* Graduations textuelles */}
      {grid.map((g, i) => (
        <text
          key={`grid-label-${i}`}
          x={cx + 4}
          y={cy - r * g - 2}
          fontSize="9"
          style={{ fill: 'var(--chart-text-muted)' }}
        >
          {effectiveMax === 1 ? `${Math.round(g * 100)}%` : formatNumber(g * effectiveMax)}
        </text>
      ))}

      {/* Polygone cibles (superposé) */}
      {hasTargets && (
        <polygon
          points={polyFor(targetPoints)}
          fillOpacity={0.1}
          strokeWidth={1.5}
          strokeDasharray="4 3"
          style={{ fill: targetColor, stroke: targetColor }}
        />
      )}

      {/* Polygone principal */}
      <polygon
        ref={pathRef}
        points={polyFor(points)}
        fillOpacity={0.3}
        strokeWidth={2}
        style={{ fill: color, stroke: color }}
      />

      {/* Points + tooltips */}
      {points.map((d, i) => {
        const [x, y] = axisPt(i, Math.max(0, Math.min(1, d.value / effectiveMax)));
        const [lx, ly] = axisPt(i, 1.22);
        const tgt = targetPoints[i];
        return (
          <g key={i}>
            <circle
              cx={x}
              cy={y}
              r={4}
              fill="#fff"
              strokeWidth={2}
              style={{ stroke: color }}
              onMouseMove={(e) =>
                showTooltip({
                  x: e.clientX,
                  y: e.clientY,
                  title: d.axis,
                  rows: [
                    { label: 'Valeur', value: formatValue(d.value) },
                    ...(tgt ? [{ label: 'Objectif', value: formatValue(tgt.value) }] : []),
                    ...(tgt ? [{ label: 'Écart', value: formatValue(d.value - tgt.value) }] : []),
                  ],
                })
              }
              onMouseLeave={hideTooltip}
            />
            <text
              x={lx}
              y={ly}
              textAnchor={lx < cx - 2 ? 'end' : lx > cx + 2 ? 'start' : 'middle'}
              fontSize={11}
              style={{ fill: 'var(--chart-text)' }}
              dy={4}
            >
              {d.axis}
            </text>
            <text
              x={lx}
              y={ly + 13}
              textAnchor={lx < cx - 2 ? 'end' : lx > cx + 2 ? 'start' : 'middle'}
              fontSize={10}
              fontWeight={600}
              style={{ fill: color }}
            >
              {formatValue(d.value)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
